import React, { useState, useEffect } from 'react'
import { NavLink, Link, useLocation } from 'react-router-dom'

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const location = useLocation()

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  // Prevent background scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
  }, [mobileMenuOpen])

  const navItems = [
    { name: 'Forside', path: '/' },
    { name: 'Tømrerarbejde', path: '/tomrerarbejde' },
    { name: 'Tag', path: '/tag' },
    { name: 'Renovering', path: '/renovering' },
    { name: 'Projekter', path: '/projekter' },
    { name: 'Om Agstner Byg', path: '/om-agstner-byg' },
    { name: 'Kontakt', path: '/kontakt' },
  ]

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-zinc-200 shadow-md rounded-none">
      {/* Top info strip */}
      <div className="hidden sm:block bg-stone-100 border-b border-zinc-200/80 px-4 py-2 text-xs text-zinc-700 uppercase tracking-wider">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center gap-2">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-2 font-semibold text-orange-600">
              <span className="w-2 h-0.5 bg-orange-500 inline-block"></span>
              <i className="fa-solid fa-location-dot" aria-hidden="true"></i>
              <span>Odensevej 36, 5492 Vissenbjerg</span>
            </span>
            <span className="hidden md:inline text-zinc-300">|</span>
            <span className="hidden md:inline text-zinc-600 font-medium">CVR: 44000997</span>
          </div>
          <div className="flex items-center gap-4 ml-auto">
            <a
              href="tel:+4552502543"
              className="inline-flex items-center gap-2 font-bold text-orange-600 hover:text-orange-700 transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-xs" aria-hidden="true"></i>
              <span className="whitespace-nowrap">Ring på 52 50 25 43</span>
            </a>
            <span className="text-zinc-300">|</span>
            <a
              href="mailto:Agstnerbyg@icloud.com"
              className="inline-flex items-center gap-2 font-medium text-zinc-700 hover:text-zinc-950 transition-colors whitespace-nowrap lowercase tracking-normal"
            >
              <i className="fa-solid fa-envelope text-xs text-orange-600" aria-hidden="true"></i>
              <span className="whitespace-nowrap">Agstnerbyg@icloud.com</span>
            </a>
            <span className="text-zinc-300">|</span>
            <a
              href="https://www.facebook.com/profile.php?id=61590981091175"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 font-semibold text-zinc-700 hover:text-orange-600 transition-colors whitespace-nowrap normal-case"
              aria-label="Agstner Byg ApS på Facebook"
            >
              <i className="fa-brands fa-facebook text-orange-600 text-sm" aria-hidden="true"></i>
              <span>Facebook</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-24 py-3 flex items-center justify-between gap-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3.5 group py-1" aria-label="Agstner Byg ApS Forside">
          <img
            src="https://assets.auxodev.dk/assets/bac61bd8-01e8-4025-a9be-38664430d08c/ag-logo.png"
            alt="Agstner Byg ApS Logo"
            className="h-14 sm:h-20 w-auto object-contain transition-transform group-hover:scale-105"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden min-[1200px]:flex items-center gap-1 font-display uppercase tracking-wider text-xs font-semibold">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `px-3.5 py-2 transition-all duration-200 rounded-none border border-transparent ${
                  isActive
                    ? 'text-orange-600 font-bold border-b-2 border-orange-500 bg-stone-100'
                    : 'text-zinc-800 hover:text-orange-600 hover:bg-stone-100'
                }`
              }
            >
              {item.name}
            </NavLink>
          ))}
        </nav>

        {/* Action Call To Action Button & Mobile Burger */}
        <div className="flex items-center gap-3">
          <Link
            to="/kontakt"
            className="hidden sm:inline-flex items-center justify-center px-5 py-2.5 rounded-none bg-orange-500 hover:bg-orange-600 text-zinc-950 font-display font-bold text-xs uppercase tracking-widest transition-all duration-200 active:scale-95 whitespace-nowrap border border-orange-500"
          >
            <i className="fa-solid fa-paper-plane mr-2 text-xs" aria-hidden="true"></i>
            Få et tilbud
          </Link>

          {/* Burger menu button */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="min-[1200px]:hidden p-2.5 rounded-none bg-stone-100 text-zinc-800 border border-zinc-300 hover:text-orange-600 hover:border-orange-500 transition-colors focus:outline-none"
            aria-expanded={mobileMenuOpen}
            aria-label="Åbn menu"
          >
            <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-xl`} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden fixed inset-0 top-[140px] z-50 bg-white border-t border-zinc-200 flex flex-col justify-between overflow-y-auto p-6 rounded-none">
          <nav className="flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `px-4 py-3.5 font-display font-bold uppercase tracking-wider text-base flex items-center justify-between transition-colors border-b border-zinc-100 rounded-none ${
                    isActive
                      ? 'text-orange-600 bg-stone-100 border-l-4 border-l-orange-500'
                      : 'text-zinc-800 hover:bg-stone-50 hover:text-orange-600'
                  }`
                }
              >
                <span>{item.name}</span>
                <i className="fa-solid fa-chevron-right text-xs opacity-60 text-orange-500" aria-hidden="true"></i>
              </NavLink>
            ))}
          </nav>

          <div className="mt-8 pt-6 border-t border-zinc-200 flex flex-col gap-4">
            <a
              href="tel:+4552502543"
              className="flex items-center justify-center gap-3 w-full py-3.5 rounded-none bg-stone-100 text-orange-600 font-display font-bold text-sm uppercase tracking-wider border border-zinc-300 hover:border-orange-500 transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone" aria-hidden="true"></i>
              <span className="whitespace-nowrap">Ring på 52 50 25 43</span>
            </a>

            <a
              href="https://www.facebook.com/profile.php?id=61590981091175"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2.5 w-full py-3 rounded-none bg-stone-100 text-zinc-800 font-display font-bold text-xs uppercase tracking-wider border border-zinc-300 hover:border-orange-500 hover:text-orange-600 transition-colors whitespace-nowrap"
            >
              <i className="fa-brands fa-facebook text-orange-600 text-base" aria-hidden="true"></i>
              <span>Følg os på Facebook</span>
            </a>

            <Link
              to="/kontakt"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-center gap-2 w-full py-3.5 rounded-none bg-orange-500 hover:bg-orange-600 text-zinc-950 font-display font-bold text-sm uppercase tracking-wider transition-all whitespace-nowrap"
            >
              <i className="fa-solid fa-paper-plane" aria-hidden="true"></i>
              <span>Få et tilbud</span>
            </Link>

            <div className="text-center text-xs text-zinc-500 mt-2 space-y-1">
              <p className="font-bold text-zinc-800 uppercase">Agstner Byg ApS</p>
              <p>Odensevej 36, 5492 Vissenbjerg</p>
              <p>CVR: 44000997</p>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
